import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FormControl, Grid, InputLabel } from "@mui/material";

import ReportePagina from "../../components/common/ReportePagina";
import FiltroFilial from "../../components/common/FiltroFilial";
import { useFiltroFilial } from "../../hooks/useFiltroFilial";
import { getsTerapiaPlanAvanceView, getsTerapiaPlanResumenView } from "../../services/terapiaPlanService";

// Reporte de plan de terapia: el avance de las puntuaciones de las sesiones
// por cada area del plan.
//
// Cada fila es un area del plan. Las columnas son las sesiones que la
// terapia tuvo dentro de las fechas del plan, y
// la ultima es el grupo de puntuacion asignado al area en el plan.
//
// El plan se elige arriba: el reporte se genera con el plan seleccionado.
const PlanTerapia = () => {
    const [idTerapiaPlan, setIdTerapiaPlan] = useState(0);
    const [listPlanes, setListPlanes] = useState([]);
    const [rows, setRows] = useState([]);
    const [cargando, setCargando] = useState(false);
    // El filtro de filial: el usuario con una filial solo ve los planes de esa.
    const { filiales, idFilial, setIdFilial, bloqueado } = useFiltroFilial();

    // La lista de planes sale del mismo resumen de la navegabilidad: las
    // terapias activas, y de ellas las que ya tienen plan.
    const fetchPlanes = useCallback(async () => {
        const data = await getsTerapiaPlanResumenView({
            idFilial: idFilial,
            member: "",
            therapist: "",
            idEstadoVigencia: 0,
            idEstado: 0,
            idTerapeuta: 0,
        }).catch((err) => { console.log("Err: ", err); });

        setListPlanes((data || []).filter((row) => row.idTerapiaPlan != null));
    }, [idFilial])

    const fetchResults = useCallback(async (idPlan) => {
        if (!idPlan || idPlan <= 0) {
            setRows([]);
            return;
        }

        setCargando(true);

        await getsTerapiaPlanAvanceView(idPlan)
            .then((data) => {
                setRows(data || []);
                setCargando(false);
            })
            .catch((err) => {
                setCargando(false);
                console.log("Err: ", err);
            });
    }, [])

    useEffect(() => {
        fetchPlanes();
    }, [fetchPlanes])

    // Las sesiones son las columnas: salen de las filas del plan, en orden
    // de fecha. Las areas sin sesiones solo aportan su fila.
    const { sesiones, areas } = useMemo(() => {
        const mapaSesiones = new Map();

        for (const row of rows) {
            if (row.idSesion != null && !mapaSesiones.has(row.idSesion)) {
                mapaSesiones.set(row.idSesion, row);
            }
        }

        // Un area por fila: la vista trae una fila por area y sesion, y para
        // el area sin sesiones una fila con IdSesion null.
        const mapaAreas = new Map();

        for (const row of rows) {
            const clave = row.idArea + "-" + row.numeroArea;

            if (!mapaAreas.has(clave)) {
                mapaAreas.set(clave, {
                    idArea: row.idArea,
                    numeroArea: row.numeroArea,
                    ordenArea: row.ordenArea,
                    codigoArea: row.codigoArea,
                    area: row.area,
                    modelo: row.modelo,
                    puntuacionGrupo: row.puntuacionGrupo,
                    puntuaciones: {},
                });
            }

            if (row.idSesion != null) {
                mapaAreas.get(clave).puntuaciones[row.idSesion] = row;
            }
        }

        return {
            sesiones: Array.from(mapaSesiones.values())
                .sort((a, b) => new Date(a.fechaSesion) - new Date(b.fechaSesion)),
            areas: Array.from(mapaAreas.values())
                .sort((a, b) => (a.modelo || "").localeCompare(b.modelo || "")
                    || (a.ordenArea - b.ordenArea)
                    || (a.area || "").localeCompare(b.area || "")),
        };
    }, [rows])

    // Datos de la terapia y del plan: todas las filas los traen, asi que
    // bastan los de la primera.
    const plan = rows.length > 0 ? rows[0] : null;

    const columnas = [
        { campo: "modelo", titulo: "Modelo", ancho: 16 },
        { campo: "area", titulo: "Area", ancho: 22 },
        ...sesiones.map((sesion, indice) => ({
            campo: "sesion" + indice,
            titulo: new Date(sesion.fechaSesion).toLocaleDateString("es-PE"),
            ancho: 12,
            alin: "center",
            render: (fila) => {
                const puntuacion = fila.puntuaciones[sesion.idSesion];

                // Sin criterios de esa area en la sesion: no hay dato.
                if (puntuacion == null || puntuacion.criteriosPuntuados == 0) return "-";

                return puntuacion.puntuacionSesion;
            },
        })),
        // Ultima columna: el grupo de puntuacion del area en el plan.
        { campo: "puntuacionGrupo", titulo: "Puntuacion", ancho: 14, alin: "center" },
    ];

    const filas = areas.map((area) => ({ ...area }));

    const textoPlan = (row) => row.participante
        + (row.fechaInicio ? " - " + new Date(row.fechaInicio).toLocaleDateString("es-PE") : "")
        + (row.fechaFin ? " al " + new Date(row.fechaFin).toLocaleDateString("es-PE") : "");

    return (
        <ReportePagina
            titulo="Plan de Terapia"
            subtitulo="Avance de las puntuaciones de las sesiones por area del plan. Las columnas son las sesiones de la terapia dentro de las fechas del plan."
            cargando={cargando}
            indicadores={[
                { etiqueta: "Areas", valor: String(areas.length), color: "#5b2d8e" },
                { etiqueta: "Sesiones", valor: String(sesiones.length), color: "#00796b" },
                { etiqueta: "Escala", valor: plan && plan.puntuacionAvance ? plan.puntuacionAvance : "-", color: "#e07b00" },
            ]}
            cabecera={plan ? [
                { etiqueta: "Participante", valor: plan.participante },
                { etiqueta: "Terapeuta", valor: plan.terapeuta },
                { etiqueta: "Terapia", valor: (plan.codigoServicio || "") + " - " + (plan.tipo || "") },
                { etiqueta: "Fecha de inicio", valor: plan.fechaInicio ? new Date(plan.fechaInicio).toLocaleDateString("es-PE") : "" },
                { etiqueta: "Fecha de fin", valor: plan.fechaFin ? new Date(plan.fechaFin).toLocaleDateString("es-PE") : "" },
                { etiqueta: "Vigencia", valor: plan.estadoVigencia || "" },
                { etiqueta: "Escala de avance", valor: plan.puntuacionAvance || "" },
            ] : []}
            filtros={
                <Grid container spacing={1}>
                    <Grid item xs={12} sm={3}>
                        <FiltroFilial
                            filiales={filiales}
                            idFilial={idFilial}
                            bloqueado={bloqueado}
                            onChange={(nuevo) => {
                                // Al cambiar de filial el plan elegido puede
                                // no ser de esa: se limpia.
                                setIdFilial(nuevo);
                                setIdTerapiaPlan(0);
                                setRows([]);
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={5}>
                        <FormControl fullWidth size="small">
                            <InputLabel htmlFor="input-plan" shrink>Plan de terapia</InputLabel>
                            <select
                                id="input-plan"
                                value={idTerapiaPlan}
                                onChange={(event) => {
                                    const plan = parseInt(event.target.value);
                                    setIdTerapiaPlan(plan);
                                    fetchResults(plan);
                                }}
                                style={{
                                    width: "100%",
                                    height: 40,
                                    padding: "0 14px",
                                    border: "1px solid rgba(0, 0, 0, 0.23)",
                                    borderRadius: 4,
                                    background: "inherit",
                                }}
                            >
                                <option value={0}>{"(Seleccione)"}</option>
                                {listPlanes.map((row) => (
                                    <option key={row.idTerapiaPlan} value={row.idTerapiaPlan}>
                                        {textoPlan(row)}
                                    </option>
                                ))}
                            </select>
                        </FormControl>
                    </Grid>
                </Grid>
            }
            columnas={columnas}
            filas={filas}
        />
    )
}

export default PlanTerapia
